import { useEffect, useRef, useState, type PointerEvent } from "react";
import { Camera, ImagePlus, RotateCcw, X } from "lucide-react";
import {
  MAX_REPORT_SCREENSHOTS,
  cropFromPoints,
  adjustCrop,
  type Crop,
  type ReportScreenshot,
} from "../shared/report-screenshots";
import {
  captureScreen,
  encodeScreenshot,
  loadScreenshot,
} from "./report-screenshots";
const full: Crop = { x: 0, y: 0, width: 1, height: 1 };
export function ReportScreenshots({
  images,
  onChange,
  disabled,
  onBusy,
  onCaptureVisibility,
  onPending,
}: {
  images: ReportScreenshot[];
  onChange: (images: ReportScreenshot[]) => void;
  disabled: boolean;
  onBusy: (busy: boolean) => void;
  onPending: (pending: boolean) => void;
  onCaptureVisibility: (hidden: boolean) => void;
}) {
  const root = useRef<HTMLElement>(null);
  const input = useRef<HTMLInputElement>(null),
    source = useRef<HTMLCanvasElement | null>(null),
    working = useRef(false),
    alive = useRef(true);
  const [url, setUrl] = useState(""),
    [crop, setCrop] = useState<Crop>(full),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const start = useRef<{ x: number; y: number } | null>(null);
  const blocked =
    disabled || busy || !!url || images.length >= MAX_REPORT_SCREENSHOTS;
  const selected =
    crop.x !== 0 || crop.y !== 0 || crop.width !== 1 || crop.height !== 1;
  function clear() {
    if (source.current) {
      source.current.width = source.current.height = 0;
      source.current = null;
    }
    setUrl("");
    setCrop(full);
  }
  useEffect(() => {
    onBusy(busy);
    onPending(!!url);
  }, [busy, url, onBusy, onPending]);
  useEffect(() => {
    alive.current = true;
    return () => {
      onBusy(false);
      onPending(false);
      alive.current = false;
      if (source.current) source.current.width = source.current.height = 0;
    };
  }, []);
  async function prepare(get: () => Promise<HTMLCanvasElement>) {
    if (working.current || blocked) return;
    working.current = true;
    setBusy(true);
    onBusy(true);
    setError("");
    try {
      const canvas = await get();
      if (!alive.current) {
        canvas.width = canvas.height = 0;
        return;
      }
      clear();
      source.current = canvas;
      setUrl(canvas.toDataURL("image/jpeg", 0.9));
    } catch (e) {
      if (alive.current)
        setError(
          (e as Error).name === "NotAllowedError"
            ? "Capture cancelled. Paste or upload a screenshot."
            : (e as Error).message,
        );
    } finally {
      working.current = false;
      if (alive.current) {
        setBusy(false);
        onBusy(false);
      }
    }
  }
  // The native listener covers paste in the message field without intercepting text.
  useEffect(() => {
    const dialog = root.current?.closest("dialog");
    if (!dialog) return;
    function paste(event: ClipboardEvent) {
      const file = Array.from(event.clipboardData?.items ?? [])
        .find((item) => item.type.startsWith("image/"))
        ?.getAsFile();
      if (!file) return;
      event.preventDefault();
      event.stopPropagation();
      if (blocked) {
        if (!disabled && !busy)
          setError(
            url
              ? "Attach or cancel this preview first."
              : "Up to three screenshots. Remove one to add another.",
          );
        return;
      }
      void prepare(() => loadScreenshot(file));
    }
    dialog.addEventListener("paste", paste);
    return () => dialog.removeEventListener("paste", paste);
  }, [blocked, disabled, busy, url, images]);
  function point(event: PointerEvent<HTMLDivElement>) {
    const r = event.currentTarget.getBoundingClientRect();
    return {
      x: (event.clientX - r.left) / r.width,
      y: (event.clientY - r.top) / r.height,
    };
  }
  function move(event: PointerEvent<HTMLDivElement>) {
    if (start.current) {
      const p = point(event);
      const next = cropFromPoints(start.current.x, start.current.y, p.x, p.y);
      if (next.width >= 0.005 && next.height >= 0.005) setCrop(next);
    }
  }
  async function attach() {
    if (
      !source.current ||
      working.current ||
      disabled ||
      images.length >= MAX_REPORT_SCREENSHOTS
    )
      return;
    working.current = true;
    setBusy(true);
    onBusy(true);
    setError("");
    try {
      const image = await encodeScreenshot(source.current, crop);
      onChange([...images, image]);
      clear();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      working.current = false;
      setBusy(false);
      onBusy(false);
    }
  }
  return (
    <section
      ref={root}
      className="report-images"
      aria-label="Screenshots"
      onDragOver={(event) => {
        if (event.dataTransfer.types.includes("Files")) event.preventDefault();
      }}
      onDrop={(event) => {
        if (!event.dataTransfer.files.length) return;
        event.preventDefault();
        event.stopPropagation();
        if (blocked) return;
        if (event.dataTransfer.files.length > 1) {
          setError("Add one screenshot at a time.");
          return;
        }
        void prepare(() => loadScreenshot(event.dataTransfer.files[0]));
      }}
    >
      {!url && (
        <div className="report-image-actions">
          {images.length < MAX_REPORT_SCREENSHOTS && (
            <>
              <button
                type="button"
                disabled={blocked}
                onClick={() => input.current?.click()}
              >
                <ImagePlus size={16} /> Add screenshot
              </button>
              {typeof navigator.mediaDevices?.getDisplayMedia ===
                "function" && (
                <button
                  type="button"
                  disabled={blocked}
                  onClick={() =>
                    void prepare(async () => {
                      onCaptureVisibility(true);
                      try {
                        return await captureScreen();
                      } finally {
                        if (alive.current) onCaptureVisibility(false);
                      }
                    })
                  }
                >
                  <Camera size={16} /> Capture screen
                </button>
              )}
            </>
          )}
          {!!images.length && (
            <span className="report-image-count">
              {images.length} / {MAX_REPORT_SCREENSHOTS}
            </span>
          )}
        </div>
      )}
      {!url && !images.length && (
        <p className="bug-help">Or paste / drop an image.</p>
      )}
      <input
        ref={input}
        type="file"
        aria-label="Choose screenshot"
        accept="image/png,image/jpeg,image/webp"
        hidden
        disabled={blocked}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) void prepare(() => loadScreenshot(file));
        }}
      />
      {busy && <p role="status">Preparing screenshot…</p>}
      {url && (
        <div className="report-crop">
          <p className="bug-help">Drag to crop out private details.</p>
          <div
            className="report-crop-image"
            onPointerDown={(e) => {
              if (busy || disabled || e.button !== 0 || !e.isPrimary) return;
              e.preventDefault();
              start.current = point(e);
              e.currentTarget.setPointerCapture(e.pointerId);
            }}
            onPointerMove={move}
            onPointerUp={(e) => {
              move(e);
              start.current = null;
            }}
            onPointerCancel={() => {
              start.current = null;
              setCrop(full);
            }}
          >
            <img src={url} alt="Screenshot to crop" draggable={false} />
            <div
              className="report-crop-selection"
              style={{
                left: `${crop.x * 100}%`,
                top: `${crop.y * 100}%`,
                width: `${crop.width * 100}%`,
                height: `${crop.height * 100}%`,
              }}
            />
          </div>
          <details>
            <summary>Adjust crop with keyboard</summary>
            <div className="report-crop-fields">
              {(["x", "y", "width", "height"] as const).map((key) => (
                <label key={key}>
                  {key}
                  <input
                    type="number"
                    aria-label={`Crop ${key} percent`}
                    min={key === "width" || key === "height" ? 1 : 0}
                    max={100}
                    step={1}
                    value={Math.round(crop[key] * 100)}
                    disabled={busy || disabled}
                    onChange={(e) =>
                      setCrop(adjustCrop(crop, key, e.target.valueAsNumber))
                    }
                  />
                </label>
              ))}
            </div>
          </details>
          <div className="report-image-actions">
            <button
              type="button"
              className="primary"
              disabled={
                busy || disabled || crop.width < 0.005 || crop.height < 0.005
              }
              onClick={() => void attach()}
            >
              {selected ? "Attach cropped area" : "Attach screenshot"}
            </button>
            {selected && (
              <button
                type="button"
                disabled={busy || disabled}
                onClick={() => setCrop(full)}
              >
                <RotateCcw size={14} /> Reset crop
              </button>
            )}
            <button type="button" disabled={busy || disabled} onClick={clear}>
              Cancel screenshot
            </button>
          </div>
        </div>
      )}
      {images.length === MAX_REPORT_SCREENSHOTS && (
        <p className="bug-help" role="status">
          Remove a screenshot to add another.
        </p>
      )}
      {!!images.length && (
        <div className="report-thumbnails">
          {images.map((image, i) => (
            <figure key={i}>
              <img src={image.dataUrl} alt={`Attached screenshot ${i + 1}`} />
              <figcaption>
                Screenshot {i + 1}
                <button
                  type="button"
                  aria-label={`Remove screenshot ${i + 1}`}
                  disabled={disabled || busy}
                  onClick={() => onChange(images.filter((_, n) => n !== i))}
                >
                  <X size={14} />
                </button>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
      {error && (
        <p role="alert" className="bug-error">
          {error}
        </p>
      )}
    </section>
  );
}
